import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Shield, MessageSquare, Clock, CheckCircle, AlertCircle, Send, RefreshCw, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const reportTypeLabels = {
  'corruzione': 'Corruzione',
  'illeciti': 'Illeciti Amministrativi',
  'discriminazione': 'Discriminazione',
  'mobbing': 'Mobbing o Molestie',
  'sicurezza': 'Violazioni Sicurezza',
  'ambiente': 'Danno Ambientale',
  'contro_odv': 'Segnalazioni contro OdV',
  'altro': 'Altro'
};

export const WhistleblowingTrack = () => {
  const [code, setCode] = useState('');
  const [report, setReport] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const searchReport = async () => {
    if (!code.trim()) {
      toast.error('Inserisci il codice di accesso');
      return;
    }

    setLoading(true);
    try {
      const reportRes = await axios.get(`${API_URL}/api/whistleblowing/reports/by-code/${code}`);
      setReport(reportRes.data);

      const messagesRes = await axios.get(`${API_URL}/api/whistleblowing/messages/${reportRes.data.id}?anonymous_code=${code}`);
      setMessages(messagesRes.data);

      toast.success('Segnalazione trovata');
    } catch (error) {
      toast.error('Codice non valido o segnalazione non trovata');
      setReport(null);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshMessages = async () => {
    if (!report) return;
    try {
      const messagesRes = await axios.get(`${API_URL}/api/whistleblowing/messages/${report.id}?anonymous_code=${code}`);
      setMessages(messagesRes.data);
      toast.success('Messaggi aggiornati');
    } catch (error) {
      toast.error('Errore nel caricare i messaggi');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API_URL}/api/whistleblowing/messages`, {
        report_id: report.id,
        anonymous_code: code,
        sender_type: 'reporter',
        message: newMessage,
      });

      toast.success('Messaggio inviato');
      setNewMessage('');
      refreshMessages();
    } catch (error) {
      toast.error('Errore invio messaggio');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ricevuta: { label: 'Ricevuta', color: 'bg-blue-500', icon: Clock },
      in_lavorazione: { label: 'In Lavorazione', color: 'bg-yellow-500', icon: RefreshCw },
      richiesta_info: { label: 'Richiesta Info', color: 'bg-orange-500', icon: AlertCircle },
      chiusa: { label: 'Chiusa', color: 'bg-green-500', icon: CheckCircle },
    };
    const config = statusConfig[status] || statusConfig.ricevuta;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Count unread messages from responsabile
  const unreadFromResponsabile = messages.filter(
    m => m.sender_type === 'responsabile' && !m.is_read
  ).length;

  return (
    <div className="min-h-screen pt-20 bg-background">
      <section className="relative py-12 bg-gradient-dark overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto text-center">
            <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
            <h1 className="font-display font-bold text-3xl lg:text-4xl mb-2 text-white">
              Traccia la tua Segnalazione
            </h1>
            <p className="text-gray-300">
              Inserisci il codice anonimo per verificare lo stato e comunicare con il responsabile
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            
            {/* Search Form */}
            {!report && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Card className="glass-card">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-2xl">Cerca Segnalazione</CardTitle>
                    <CardDescription>
                      Inserisci il codice che hai ricevuto al momento della segnalazione
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="max-w-md mx-auto space-y-4">
                      <div>
                        <Label htmlFor="code">Codice di Accesso Anonimo</Label>
                        <Input 
                          id="code" 
                          value={code} 
                          onChange={(e) => setCode(e.target.value)} 
                          onKeyPress={(e) => e.key === 'Enter' && searchReport()}
                          placeholder="es. Y9swu_xaPKq65joNgt-G_Q" 
                          className="mt-2 font-mono"
                          data-testid="track-code-input"
                        />
                      </div>
                      <Button 
                        onClick={searchReport} 
                        disabled={loading} 
                        className="w-full" 
                        size="lg"
                        data-testid="track-search-btn"
                      >
                        {loading ? 'Ricerca...' : <><Search className="w-4 h-4 mr-2" />Cerca Segnalazione</>}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Report Details */}
            {report && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                
                {/* Report Header Card */}
                <Card className="glass-card border-primary/20">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-2xl">{report.title}</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          ID: <code className="bg-muted px-2 py-0.5 rounded">{report.id.slice(0, 8)}...</code>
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(report.status)}
                        <Badge variant="outline" className="capitalize">{report.priority}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-4 p-4 rounded-lg bg-muted/30">
                      <div>
                        <p className="text-sm text-muted-foreground">Categoria</p>
                        <p className="font-medium">{reportTypeLabels[report.report_type] || report.report_type}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Data Segnalazione</p>
                        <p className="font-medium">{formatDate(report.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Ultimo Aggiornamento</p>
                        <p className="font-medium">{formatDate(report.updated_at)}</p>
                      </div>
                    </div>

                    {report.status === 'richiesta_info' && (
                      <Alert className="mt-4 border-orange-500/20 bg-orange-500/5">
                        <AlertCircle className="h-4 w-4 text-orange-500" />
                        <AlertDescription className="ml-2">
                          <strong>Il responsabile ha richiesto ulteriori informazioni.</strong><br />
                          Controlla i messaggi qui sotto e rispondi.
                        </AlertDescription>
                      </Alert>
                    )}

                    {report.status === 'chiusa' && (
                      <Alert className="mt-4 border-green-500/20 bg-green-500/5">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <AlertDescription className="ml-2">
                          <strong>La segnalazione è stata chiusa.</strong><br />
                          Puoi ancora visualizzare i messaggi ma non inviarne di nuovi.
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button 
                      onClick={() => { setReport(null); setMessages([]); setCode(''); }} 
                      variant="outline" 
                      className="w-full mt-4"
                    >
                      Nuova Ricerca
                    </Button>
                  </CardContent>
                </Card>

                {/* Communications Card */}
                <Card className="glass-card">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" />
                        Comunicazioni
                        {unreadFromResponsabile > 0 && (
                          <Badge className="bg-primary">{unreadFromResponsabile} nuovi</Badge>
                        )}
                      </CardTitle>
                      <Button variant="ghost" size="sm" onClick={refreshMessages}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Aggiorna
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Messages List */}
                    <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                      {messages.length === 0 ? (
                        <div className="text-center py-8">
                          <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                          <p className="text-muted-foreground">Nessuna comunicazione</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Le risposte del responsabile appariranno qui
                          </p>
                        </div>
                      ) : (
                        messages.map((msg) => (
                          <div 
                            key={msg.id} 
                            className={`p-4 rounded-lg ${
                              msg.sender_type === 'reporter' 
                                ? 'bg-primary/10 ml-8 border-l-2 border-primary' 
                                : 'bg-muted/50 mr-8 border-l-2 border-amber-500'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm font-medium">
                                  {msg.sender_type === 'reporter' ? 'Tu' : 'Responsabile'}
                                </span>
                                {msg.sender_type === 'responsabile' && (
                                  <Badge variant="outline" className="text-xs">OdV</Badge>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(msg.created_at)}
                              </span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          </div>
                        ))
                      )}
                    </div>

                    {/* New Message Input */}
                    {report.status !== 'chiusa' ? (
                      <div className="space-y-3 pt-4 border-t border-border">
                        <Label htmlFor="newMessage">Invia un messaggio al Responsabile</Label>
                        <Textarea 
                          id="newMessage"
                          value={newMessage} 
                          onChange={(e) => setNewMessage(e.target.value)} 
                          rows={3} 
                          placeholder="Scrivi qui il tuo messaggio..." 
                          className="resize-none"
                          data-testid="track-message-input"
                        />
                        <Button 
                          onClick={sendMessage} 
                          disabled={!newMessage.trim() || sending} 
                          className="w-full"
                          data-testid="track-send-btn"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {sending ? 'Invio...' : 'Invia Messaggio'}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground text-sm pt-4 border-t border-border">
                        La segnalazione è chiusa. Non è possibile inviare nuovi messaggi.
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Security Note */}
                <Alert className="border-primary/20 bg-primary/5">
                  <Shield className="h-4 w-4 text-primary" />
                  <AlertDescription className="ml-2 text-sm">
                    <strong>La tua identità è protetta.</strong> Le comunicazioni sono criptate e il tuo anonimato è garantito.
                    Conserva il tuo codice di accesso in un luogo sicuro.
                  </AlertDescription>
                </Alert>
              </motion.div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhistleblowingTrack;
